/**
 * Session Monitor Hook
 * Provides session monitoring capabilities to components
 */

import { useCallback, useEffect, useState } from 'react';
import { sessionMonitor } from '@/services/session/sessionMonitor';
import type { SessionConfig, SessionTimerInfo } from '@/types/session';

export interface UseSessionMonitorOptions {
  onSessionExpired?: () => void;
  onSessionWarning?: (timeRemaining: number) => void;
  onIdleWarning?: (timeRemaining: number) => void;
  onTokenRefreshed?: () => void;
  onActivityDetected?: () => void;
  config?: Partial<SessionConfig>;
  autoStart?: boolean;
}

export interface UseSessionMonitorResult {
  // Session state
  sessionInfo: SessionTimerInfo;
  isAuthenticated: boolean;

  // Actions
  startMonitoring: () => void;
  stopMonitoring: () => void;
  refreshToken: () => Promise<boolean>;
  extendSession: () => Promise<boolean>;
  recordActivity: () => void;

  // Configuration
  configure: (config: Partial<SessionConfig>) => void;

  // Utilities
  formatTime: (seconds: number) => string;
}

/**
 * Hook to monitor session state and provide session management actions
 */
export const useSessionMonitor = (
  options: UseSessionMonitorOptions = {},
): UseSessionMonitorResult => {
  const {
    onSessionExpired,
    onSessionWarning,
    onIdleWarning,
    onTokenRefreshed,
    onActivityDetected,
    config,
    autoStart = false,
  } = options;

  const [sessionInfo, setSessionInfo] = useState<SessionTimerInfo>({
    tokenExpiresIn: 0,
    idleTimeRemaining: Infinity,
    isAboutToExpire: false,
    isIdle: false,
  });

  const [isAuthenticated, _setIsAuthenticated] = useState(false);

  /**
   * Update session info
   */
  const updateSessionInfo = useCallback(() => {
    const info = sessionMonitor.getSessionTimerInfo();
    setSessionInfo(info);
  }, []);

  /**
   * Start monitoring
   */
  const startMonitoring = useCallback(() => {
    sessionMonitor.start();
    updateSessionInfo();
  }, [updateSessionInfo]);

  /**
   * Stop monitoring
   */
  const stopMonitoring = useCallback(() => {
    sessionMonitor.stop();
  }, []);

  /**
   * Refresh token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    return await sessionMonitor.refreshToken();
  }, []);

  /**
   * Extend session
   */
  const extendSession = useCallback(async (): Promise<boolean> => {
    return await sessionMonitor.extendSession();
  }, []);

  /**
   * Record activity
   */
  const recordActivity = useCallback(() => {
    sessionMonitor.recordActivity();
  }, []);

  /**
   * Configure session monitor
   */
  const configure = useCallback((newConfig: Partial<SessionConfig>) => {
    sessionMonitor.configure(newConfig);
  }, []);

  /**
   * Format time in human-readable format
   */
  const formatTime = useCallback((seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Initialize and register event handlers
   */
  useEffect(() => {
    // Configure if config provided
    if (config) {
      sessionMonitor.configure(config);
    }

    // Register event handlers
    if (onSessionExpired) {
      sessionMonitor.onSessionExpired(onSessionExpired);
    }

    if (onSessionWarning) {
      sessionMonitor.onSessionWarning(onSessionWarning);
    }

    if (onIdleWarning) {
      sessionMonitor.onIdleWarning(onIdleWarning);
    }

    if (onTokenRefreshed) {
      sessionMonitor.onTokenRefreshed(onTokenRefreshed);
    }

    if (onActivityDetected) {
      sessionMonitor.onActivityDetected(onActivityDetected);
    }

    // Auto-start if enabled
    if (autoStart) {
      startMonitoring();
    }

    // Update session info periodically
    const interval = setInterval(updateSessionInfo, 1000);

    return () => {
      clearInterval(interval);
      stopMonitoring();
    };
  }, [
    config,
    autoStart,
    onSessionExpired,
    onSessionWarning,
    onIdleWarning,
    onTokenRefreshed,
    onActivityDetected,
    startMonitoring,
    stopMonitoring,
    updateSessionInfo,
  ]);

  return {
    sessionInfo,
    isAuthenticated,
    startMonitoring,
    stopMonitoring,
    refreshToken,
    extendSession,
    recordActivity,
    configure,
    formatTime,
  };
};

export default useSessionMonitor;
